/**
 * 波形 peaks から、生成サムネイルを動かすための値を取り出す。
 *
 * 入力は peaks.ts の WaveformPeaks（20 ペア/秒・チャンネル別 min/max・
 * [-1, 1] 正規化済み）。ここで取るのは「いつどれだけ鳴っているか」だけで、
 * 何の音かの判定は行わない。peaks には周波数情報が無く、実測スペクトルによる
 * 判定（spectrumCategory）の方が確実なため。
 *
 * エンベロープは再生位置に対する音量として、BPM はビートパルスの減衰時定数と
 * して audioDriver が使う。
 */

import { WaveformPeaks, PEAKS_PAIRS_PER_SECOND } from './peaks';

export interface PeaksMotion {
  /** モノラル化した音量エンベロープ（PEAKS_PAIRS_PER_SECOND Hz・[0, 1]） */
  envelope: Float32Array;
  /** 推定 BPM。自己相関にピークが立たない（無音・定常音）場合は null */
  bpm: number | null;
}

/** 自己相関を試みる最小フレーム数（= 3 秒） */
const MIN_FRAMES_FOR_TEMPO = PEAKS_PAIRS_PER_SECOND * 3;
/** 自己相関のラグ範囲。6 フレーム = 200BPM、40 フレーム = 30BPM */
const MIN_TEMPO_LAG = 6;
const MAX_TEMPO_LAG = 40;
/** BPM をこの範囲に倍・半分で折り返す */
const BPM_FOLD_MIN = 70;
const BPM_FOLD_MAX = 180;

const EMPTY_MOTION: PeaksMotion = { envelope: new Float32Array(0), bpm: null };

/** チャンネルの min/max 交互配列を絶対値エンベロープに変換する */
function channelToEnvelope(channel: number[]): Float32Array {
  const pairs = Math.floor(channel.length / 2);
  const env = new Float32Array(pairs);
  for (let i = 0; i < pairs; i++) {
    const min = channel[i * 2];
    const max = channel[i * 2 + 1];
    env[i] = Math.max(Math.abs(min), Math.abs(max));
  }
  return env;
}

/** 各チャンネルのエンベロープを平均してモノラル化する */
function toMonoEnvelope(channels: Float32Array[]): Float32Array {
  if (channels.length === 1) return channels[0];
  const length = Math.min(...channels.map(c => c.length));
  const mono = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (const ch of channels) sum += ch[i];
    mono[i] = sum / channels.length;
  }
  return mono;
}

/**
 * エンベロープの正の差分（オンセット強度の近似）。
 * 先頭フレームは無音からの立ち上がりとみなす（頭から鳴り始める効果音を拾うため）。
 */
function computeFlux(envelope: Float32Array): Float32Array {
  const flux = new Float32Array(envelope.length);
  if (envelope.length === 0) return flux;
  flux[0] = envelope[0];
  for (let i = 1; i < envelope.length; i++) {
    const delta = envelope[i] - envelope[i - 1];
    flux[i] = delta > 0 ? delta : 0;
  }
  return flux;
}

/** BPM を BPM_FOLD_MIN〜BPM_FOLD_MAX に倍・半分で折り返す */
function foldBpm(bpm: number): number {
  let folded = bpm;
  while (folded < BPM_FOLD_MIN) folded *= 2;
  while (folded > BPM_FOLD_MAX) folded /= 2;
  return folded;
}

/** オンセット列の自己相関からテンポを推定する */
function estimateBpm(flux: Float32Array): number | null {
  const n = flux.length;
  if (n < MIN_FRAMES_FOR_TEMPO) return null;

  let mean = 0;
  for (let i = 0; i < n; i++) mean += flux[i];
  mean /= n;

  const centered = new Float32Array(n);
  let energy = 0;
  for (let i = 0; i < n; i++) {
    centered[i] = flux[i] - mean;
    energy += centered[i] * centered[i];
  }
  if (energy <= 0) return null;

  const maxLag = Math.min(MAX_TEMPO_LAG, Math.floor(n / 2));
  let bestLag = 0;
  let bestScore = 0;
  for (let lag = MIN_TEMPO_LAG; lag <= maxLag; lag++) {
    let sum = 0;
    for (let i = lag; i < n; i++) sum += centered[i] * centered[i - lag];
    const score = sum / energy;
    if (score > bestScore) {
      bestScore = score;
      bestLag = lag;
    }
  }
  if (bestLag === 0) return null;

  return foldBpm((PEAKS_PAIRS_PER_SECOND * 60) / bestLag);
}

/**
 * peaks からエンベロープとテンポを取り出す。
 * 重い処理ではないが結果は不変なので、呼び出し側で useMemo すること。
 */
export function analyzePeaks(peaks: WaveformPeaks): PeaksMotion {
  const channels = peaks.filter(ch => ch.length >= 2).map(channelToEnvelope);
  if (channels.length === 0) return EMPTY_MOTION;

  const envelope = toMonoEnvelope(channels);
  if (envelope.length === 0) return EMPTY_MOTION;

  return { envelope, bpm: estimateBpm(computeFlux(envelope)) };
}

/**
 * 再生位置（秒）のエンベロープ値を線形補間で取得する。
 * 範囲外・空エンベロープは 0。
 */
export function sampleEnvelope(envelope: Float32Array, timeSec: number): number {
  const n = envelope.length;
  if (n === 0 || !Number.isFinite(timeSec) || timeSec < 0) return 0;

  const pos = timeSec * PEAKS_PAIRS_PER_SECOND;
  if (pos >= n - 1) return envelope[n - 1];

  const i = Math.floor(pos);
  const frac = pos - i;
  return envelope[i] * (1 - frac) + envelope[i + 1] * frac;
}
